import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ArrowLeft, Eye, GraduationCap, TrendingUp, CheckCircle2, Mail, Lock } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const initialMode = 'investor';
  const [mode] = useState(initialMode);
  const [step, setStep] = useState('form'); // form | otp | student_done
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '', branch_id: '', course_id: '', sales_code: '' });
  const [salesName, setSalesName] = useState('');
  const codeLocked = !!searchParams.get('sp');
  const [showPassword, setShowPassword] = useState(false);
  const [branches, setBranches] = useState([]);
  const [tempToken, setTempToken] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [studentInfo, setStudentInfo] = useState(null); // {student_id, email}
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/auth/public/branches').then(res => setBranches(res.data)).catch(() => {});
    const cid = searchParams.get('course');
    if (cid) setForm(f => ({ ...f, course_id: cid }));
    const sp = searchParams.get('sp');
    if (sp) setForm(f => ({ ...f, sales_code: sp.toUpperCase() }));
  }, [searchParams]);

  useEffect(() => {
    setSalesName('');
    const code = form.sales_code.trim();
    if (!/^SP-\d+$/i.test(code)) return;
    const t = setTimeout(() => {
      api.get(`/sales/public/validate/${code}`).then(r => setSalesName(r.data.name)).catch(() => setSalesName('__invalid__'));
    }, 350);
    return () => clearTimeout(t);
  }, [form.sales_code]);

  const handleInvestorRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!form.branch_id) { setError('Please select your branch'); return; }
    if (!form.sales_code.trim()) { setError('Sales Person Code is required'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', {
        email: form.email, password: form.password, name: form.name, phone: form.phone,
        branch_id: form.branch_id, sales_code: form.sales_code.trim(),
      });
      if (data.requires_verification) { setTempToken(data.temp_token); setStep('otp'); toast.success('OTP sent to your email'); }
    } catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const handleStudentRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) { setError('Name and email are required'); return; }
    setLoading(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() };
      if (form.course_id) payload.course_id = form.course_id;
      const { data } = await api.post('/students/register', payload);
      setStudentInfo({ student_id: data.student_id || data.student_code, email: form.email.trim() });
      setStep('student_done');
      toast.success('Registration successful! Check your email for login credentials.');
    } catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/verify-registration', { temp_token: tempToken, otp_code: otp });
      toast.success('Account verified! Please complete your KYC.');
      await checkAuth();
      navigate('/investor');
    } catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    try { await api.post('/auth/resend-otp', { temp_token: tempToken, purpose: 'registration' }); toast.success('New OTP sent'); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const isStudent = mode === 'student';
  const accent = isStudent ? 'sky' : 'emerald'; // dynamic color theming

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* LEFT HERO */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-end p-12"
           style={{ background: isStudent
             ? 'linear-gradient(135deg,#0C4A6E 0%,#075985 50%,#0369A1 100%)'
             : 'linear-gradient(135deg,#064E3B 0%,#065F46 50%,#047857 100%)' }}>
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(circle at 30% 60%, black 40%, transparent 78%)',
        }} />
        <div className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full bg-white/10 blur-[120px]" />
        <div className="relative z-10 text-white max-w-lg">
          <img src="/quantora-logo.png" alt="Quantora" className="w-14 h-14 rounded-xl mb-6 shadow-2xl ring-2 ring-white/20" />
          {isStudent ? (
            <>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] tracking-[0.25em] uppercase font-semibold mb-4">
                <GraduationCap size={12} /> Quantora Academy
              </div>
              <h1 className="font-heading text-4xl xl:text-5xl font-black tracking-tight leading-tight mb-4">
                Learn the markets from<br />practicing traders.
              </h1>
              <p className="text-white/80 leading-relaxed">Certified courses, live projects and a real trading floor mentor — everything you need to go from beginner to placement-ready.</p>
              <ul className="mt-8 space-y-2 text-sm text-white/85">
                {['Auto-emailed login credentials', 'Free video lessons + auto-graded exams', 'Digital certificates & student ID card', '18% GST invoice auto-generated'].map(x => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-sky-200" /> {x}</li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] tracking-[0.25em] uppercase font-semibold mb-4">
                <TrendingUp size={12} /> Market Advisory
              </div>
              <h1 className="font-heading text-4xl xl:text-5xl font-black tracking-tight leading-tight mb-4">
                Read the market<br />with clarity.
              </h1>
              <p className="text-white/80 leading-relaxed">Open your Quantora account to receive timely trading signals and research-backed market advisory on your phone.</p>
              <ul className="mt-8 space-y-2 text-sm text-white/85">
                {['Timely trading signals', 'Research-backed market views', 'Real-time alerts', 'Secure, encrypted access'].map(x => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-200" /> {x}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          {!searchParams.get('sp') && step === 'form' && (
            <div className="text-center py-6" data-testid="register-invite-only">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4"><Lock size={22} className="text-slate-600" /></div>
              <h2 className="font-heading text-2xl font-black text-slate-900 tracking-tight mb-2">Registration by invitation only</h2>
              <p className="text-sm text-slate-500 mb-6">Please use the registration link shared by your Quantora sales person.</p>
              <Link to="/login" className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm" data-testid="invite-only-login-link">Go to Sign In</Link>
            </div>
          )}

          {searchParams.get('sp') && step === 'form' && (
            <>
              <div className="mb-6">
                <h2 className="font-heading text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                  {isStudent ? 'Join the Academy' : 'Create Account'}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {isStudent ? 'We\u2019ll email your login credentials in seconds.' : 'Register with your sales person\u2019s invitation.'}
                </p>
              </div>

              {error && <div className="p-3 mb-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm" data-testid="register-error">{error}</div>}

              {isStudent ? (
                <form onSubmit={handleStudentRegister} className="space-y-3" data-testid="student-register-form">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Full Name *</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required data-testid="register-name-input" className="bg-slate-50 border-slate-200 rounded-xl h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required data-testid="register-email-input" className="bg-slate-50 border-slate-200 rounded-xl h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Phone (optional)</Label>
                    <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91..." data-testid="register-phone-input" className="bg-slate-50 border-slate-200 rounded-xl h-11" />
                  </div>
                  {/* Optional course dropdown removed — /api/courses currently requires auth, so this
                      block is intentionally left out on the public register page. Students can pick a
                      course after signing in. */}
                  <p className="text-[11px] text-slate-500 pt-1">A random password will be generated and emailed to you along with your student ID.</p>
                  <Button type="submit" disabled={loading} className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl h-11 mt-2" data-testid="register-submit-btn">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : (<><GraduationCap size={16} className="mr-2" /> Create Student Account</>)}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleInvestorRegister} className="space-y-3" data-testid="investor-register-form">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Full Name *</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required data-testid="register-name-input" className="bg-slate-50 border-slate-200 rounded-xl h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required data-testid="register-email-input" className="bg-slate-50 border-slate-200 rounded-xl h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91..." data-testid="register-phone-input" className="bg-slate-50 border-slate-200 rounded-xl h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Select Branch *</Label>
                    <Select value={form.branch_id} onValueChange={v => setForm({ ...form, branch_id: v })}>
                      <SelectTrigger className="bg-slate-50 border-slate-200 rounded-xl h-11" data-testid="register-branch-select"><SelectValue placeholder="Choose your branch" /></SelectTrigger>
                      <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.city ? ` — ${b.city}` : ''}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Password *</Label>
                      <div className="relative">
                        <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 6 chars" required data-testid="register-password-input" className="bg-slate-50 border-slate-200 rounded-xl h-11 pr-9" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" tabIndex={-1}><Eye size={15} /></button>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Confirm *</Label>
                      <Input type={showPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} placeholder="Repeat" required data-testid="register-confirm-input" className="bg-slate-50 border-slate-200 rounded-xl h-11" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Sales Person Code *</Label>
                    <Input value={form.sales_code} readOnly={codeLocked} onChange={e => setForm({ ...form, sales_code: e.target.value.toUpperCase() })} placeholder="e.g. SP-001" required data-testid="register-sales-code-input" className={`border-slate-200 rounded-xl h-11 font-mono ${codeLocked ? 'bg-slate-100 text-emerald-700 cursor-not-allowed' : 'bg-slate-50'}`} />
                    {codeLocked && <p className="text-[11px] text-slate-500 flex items-center gap-1" data-testid="sales-code-locked"><Lock size={11} /> Filled in from your invitation link</p>}
                    {salesName && salesName !== '__invalid__' && <p className="text-xs text-emerald-700" data-testid="sales-code-valid">Sales person: {salesName}</p>}
                    {salesName === '__invalid__' && <p className="text-xs text-rose-600" data-testid="sales-code-invalid">Code not found. Please check with your sales person.</p>}
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11 mt-2" data-testid="register-submit-btn">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : (<><TrendingUp size={16} className="mr-2" /> Create Investor Account</>)}
                  </Button>
                </form>
              )}

              <p className="mt-6 text-center text-sm text-slate-500">
                Already have an account? <Link to="/login" className={`font-medium hover:underline ${isStudent ? 'text-sky-700' : 'text-emerald-700'}`} data-testid="login-link">Sign In</Link>
              </p>
            </>
          )}

          {step === 'otp' && (
            <>
              <button onClick={() => setStep('form')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-form-btn"><ArrowLeft size={16} /> Back</button>
              <div className="mb-8">
                <h2 className="font-heading text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Verify Email</h2>
                <p className="mt-2 text-sm text-slate-500">Enter the 6-digit OTP sent to <strong>{form.email}</strong></p>
              </div>
              {error && <div className="p-3 mb-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm" data-testid="otp-error">{error}</div>}
              <div className="flex justify-center mb-6">
                <InputOTP value={otp} onChange={setOtp} maxLength={6} data-testid="register-otp-input">
                  <InputOTPGroup><InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} /><InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} /></InputOTPGroup>
                </InputOTP>
              </div>
              <Button onClick={handleVerifyOtp} disabled={loading || otp.length !== 6} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11" data-testid="verify-otp-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Verify & Create Account'}
              </Button>
              <button onClick={handleResendOtp} className="w-full mt-4 text-sm text-slate-500 hover:text-emerald-700 text-center" data-testid="resend-otp-btn">Didn&apos;t receive OTP? Resend</button>
            </>
          )}

          {step === 'student_done' && (
            <div className="text-center py-4" data-testid="student-register-done">
              <div className="w-16 h-16 mx-auto rounded-full bg-sky-100 flex items-center justify-center mb-4">
                <Mail size={28} className="text-sky-700" />
              </div>
              <h2 className="font-heading text-2xl font-black text-slate-900 tracking-tight mb-2">Check your inbox</h2>
              <p className="text-sm text-slate-500 mb-1">We&apos;ve sent your login credentials to <strong className="text-slate-900">{studentInfo?.email}</strong>.</p>
              {studentInfo?.student_id && (
                <p className="text-sm text-slate-500 mb-6">Your student ID: <span className="font-mono font-bold text-slate-900">{studentInfo.student_id}</span></p>
              )}
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl text-left text-sm text-sky-900 mb-6">
                <p className="font-semibold mb-1">Next steps</p>
                <ol className="list-decimal list-inside space-y-0.5 text-sky-800">
                  <li>Open the credentials email from Quantora Academy</li>
                  <li>Return here and sign in with the emailed password</li>
                  <li>Complete your one-time KYC to unlock the portal</li>
                </ol>
              </div>
              <Link to="/login" className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm" data-testid="go-to-login">
                Go to Sign In
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
